import React, { useState, useEffect, useRef, useCallback } from 'react';
import Chart from 'chart.js/auto';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { 
  convertTemperature, 
  convertPressure, 
  convertWindSpeed,
  convertPrecipitation,
  getTemperatureUnit,
  getPressureUnit,
  getWindSpeedUnit,
  getPrecipitationUnit
} from '../utils/unitConversion';

interface WeatherHistoryData {
  daily: Array<{
    date: string;
    temperature?: number;
    tempMin: number;
    tempMax: number;
    humidity: number;
    pressure: number;
    windSpeed: number;
    windGust?: number;
    windDirection: number;
    precipitation: number;
    visibility: number;
    weather_code: number;
    cloudiness: number;
    sunrise?: string;
    sunset?: string;
    moonPhase: number;
    fishingScore?: {
      score: number;
      factors?: any[];
      criticalFactors?: any[];
    };
  }>;
}

interface CatchWeatherTrendsProps {
  weatherHistory: WeatherHistoryData;
  catchTimestamp: number;
}

type TrendTab = 'temperature' | 'pressure' | 'wind' | 'windDirection' | 'precipitation' | 'fishingScore' | 'astronomy';

export const CatchWeatherTrends: React.FC<CatchWeatherTrendsProps> = ({ 
  weatherHistory, 
  catchTimestamp 
}) => {
  const { t } = useLanguage();
  const { unitSystem } = useSettings();
  const [activeTab, setActiveTab] = useState<TrendTab>('temperature');
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  // Helper function to get moon phase icon
  const getMoonPhaseIcon = useCallback((phase: number): string => {
    if (phase < 0.03) return '🌑';
    if (phase < 0.22) return '🌒';
    if (phase < 0.28) return '🌓';
    if (phase < 0.47) return '🌔';
    if (phase < 0.53) return '🌕';
    if (phase < 0.72) return '🌖';
    if (phase < 0.78) return '🌗';
    if (phase < 0.97) return '🌘';
    return '🌑';
  }, []);

  // Helper function to get translated moon phase label
  const getLocalizedMoonPhase = useCallback((phase: number): string => {
    if (phase < 0.03) return t.newMoon || 'New Moon';
    if (phase < 0.22) return t.waxingCrescent || 'Waxing Crescent';
    if (phase < 0.28) return t.firstQuarter || 'First Quarter';
    if (phase < 0.47) return t.waxingGibbous || 'Waxing Gibbous';
    if (phase < 0.53) return t.fullMoon || 'Full Moon';
    if (phase < 0.72) return t.waningGibbous || 'Waning Gibbous';
    if (phase < 0.78) return t.lastQuarter || 'Last Quarter';
    if (phase < 0.97) return t.waningCrescent || 'Waning Crescent';
    return t.newMoon || 'New Moon';
  }, [t]);

  // Helper function to get translated wind direction
  const getLocalizedWindDirection = useCallback((degrees: number): string => {
    const directions = [
      t.north || 'N',
      t.northeast || 'NE', 
      t.east || 'E',
      t.southeast || 'SE',
      t.south || 'S',
      t.southwest || 'SW',
      t.west || 'W',
      t.northwest || 'NW'
    ];
    const index = Math.round(((degrees % 360) / 45)) % 8;
    return directions[index];
  }, [t]);

  useEffect(() => {
    if (!chartRef.current || !weatherHistory.daily || weatherHistory.daily.length === 0) return;

    // Destroy previous chart
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Debug: Check what data we have
    console.log('📊 CatchWeatherTrends - Daily data:', weatherHistory.daily);
    console.log('📊 CatchWeatherTrends - Catch timestamp:', new Date(catchTimestamp).toISOString());

    // Use daily data directly - much cleaner and more meaningful for trends!
    const dailyData = [...weatherHistory.daily].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Create labels (date format)
    const labels = dailyData.map(day => {
      const date = new Date(day.date);
      return date.toLocaleDateString('sv-SE', { 
        month: 'short', 
        day: 'numeric'
      });
    });

    // Find catch day index for annotation
    const catchDate = new Date(catchTimestamp).toISOString().split('T')[0];
    const catchIndex = dailyData.findIndex(d => d.date === catchDate);

    let datasets: any[] = [];
    let yAxisConfig: any = {};

    switch (activeTab) {
      case 'temperature':
        datasets = [
          {
            label: `${t.temperature || 'Temperatur'} (${getTemperatureUnit(unitSystem)})`,
            data: dailyData.map(d => convertTemperature((d.tempMin + d.tempMax) / 2, unitSystem)),
            borderColor: 'rgba(239, 68, 68, 0.8)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            tension: 0.4,
            fill: true,
            pointBackgroundColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(34, 197, 94, 1)' : 'rgba(239, 68, 68, 0.8)'
            ),
            pointBorderColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(34, 197, 94, 1)' : 'rgba(239, 68, 68, 0.8)'
            ),
            pointRadius: dailyData.map((_, idx) => idx === catchIndex ? 8 : 4),
            pointBorderWidth: dailyData.map((_, idx) => idx === catchIndex ? 3 : 1),
          }
        ];
        yAxisConfig = {
          title: { 
            display: true, 
            text: `${t.temperature || 'Temperatur'} (${getTemperatureUnit(unitSystem)})`, 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          }
        };
        break;

      case 'pressure':
        datasets = [
          {
            label: `${t.pressure || 'Lufttryck'} (${getPressureUnit(unitSystem)})`,
            data: dailyData.map(d => convertPressure(d.pressure, unitSystem)),
            borderColor: 'rgba(59, 130, 246, 0.8)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            tension: 0.4,
            fill: true,
            pointBackgroundColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(34, 197, 94, 1)' : 'rgba(59, 130, 246, 0.8)'
            ),
            pointBorderColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(34, 197, 94, 1)' : 'rgba(59, 130, 246, 0.8)'
            ),
            pointRadius: dailyData.map((_, idx) => idx === catchIndex ? 8 : 4),
            pointBorderWidth: dailyData.map((_, idx) => idx === catchIndex ? 3 : 1),
          }
        ];
        yAxisConfig = {
          title: { 
            display: true, 
            text: `${t.pressure || 'Lufttryck'} (${getPressureUnit(unitSystem)})`, 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          }
        };
        break;

      case 'wind':
        datasets = [
          {
            label: `${t.wind || 'Vind'} (${getWindSpeedUnit(unitSystem)})`,
            data: dailyData.map(d => convertWindSpeed(d.windSpeed, unitSystem)),
            borderColor: 'rgba(34, 197, 94, 0.8)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            tension: 0.4,
            fill: true,
            pointBackgroundColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(239, 68, 68, 1)' : 'rgba(34, 197, 94, 0.8)'
            ),
            pointBorderColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(239, 68, 68, 1)' : 'rgba(34, 197, 94, 0.8)'
            ),
            pointRadius: dailyData.map((_, idx) => idx === catchIndex ? 8 : 4),
            pointBorderWidth: dailyData.map((_, idx) => idx === catchIndex ? 3 : 1),
          }
        ];
        
        // Add wind gusts if available
        if (dailyData.some(d => d.windGust !== undefined)) {
          datasets.push({
            label: `${t.windGust || 'Byar'} (${getWindSpeedUnit(unitSystem)})`,
            data: dailyData.map(d => d.windGust ? convertWindSpeed(d.windGust, unitSystem) : null),
            borderColor: 'rgba(239, 68, 68, 0.6)',
            backgroundColor: 'rgba(239, 68, 68, 0.05)',
            borderDash: [5, 5],
            tension: 0.4,
            fill: false,
            pointRadius: 2,
          });
        }
        
        yAxisConfig = {
          title: { 
            display: true, 
            text: `${t.windSpeed || 'Vindhastighet'} (${getWindSpeedUnit(unitSystem)})`, 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          }
        };
        break;

      case 'precipitation':
        datasets = [
          {
            label: `${t.precipitation || 'Nederbörd'} (${getPrecipitationUnit(unitSystem)})`,
            data: dailyData.map(d => convertPrecipitation(d.precipitation, unitSystem)),
            borderColor: 'rgba(59, 130, 246, 0.8)',
            backgroundColor: 'rgba(59, 130, 246, 0.2)',
            tension: 0.4,
            fill: true,
            pointBackgroundColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(34, 197, 94, 1)' : 'rgba(59, 130, 246, 0.8)'
            ),
            pointBorderColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(34, 197, 94, 1)' : 'rgba(59, 130, 246, 0.8)'
            ),
            pointRadius: dailyData.map((_, idx) => idx === catchIndex ? 8 : 4),
            pointBorderWidth: dailyData.map((_, idx) => idx === catchIndex ? 3 : 1),
          }
        ];
        yAxisConfig = {
          title: { 
            display: true, 
            text: `${t.precipitation || 'Nederbörd'} (${getPrecipitationUnit(unitSystem)})`, 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          }
        };
        break;

      case 'fishingScore':
        datasets = [
          {
            label: `${t.fishingScore || 'Fiskescore'} (0-100)`,
            data: dailyData.map(d => (d as any).fishingScore?.score || null),
            borderColor: 'rgba(251, 191, 36, 0.8)',
            backgroundColor: 'rgba(251, 191, 36, 0.1)',
            tension: 0.4,
            fill: true,
            pointBackgroundColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(34, 197, 94, 1)' : 'rgba(251, 191, 36, 0.8)'
            ),
            pointBorderColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(34, 197, 94, 1)' : 'rgba(251, 191, 36, 0.8)'
            ),
            pointRadius: dailyData.map((_, idx) => idx === catchIndex ? 8 : 4),
            pointBorderWidth: dailyData.map((_, idx) => idx === catchIndex ? 3 : 1),
          }
        ];
        yAxisConfig = {
          min: 0,
          max: 100,
          title: { 
            display: true, 
            text: `${t.fishingScore || 'Fiskescore'} (0-100)`, 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          }
        };
        break;

      case 'windDirection':
        datasets = [
          {
            label: `${t.windDirection || 'Vindriktning'} (°)`,
            data: dailyData.map(d => d.windDirection),
            borderColor: 'rgba(147, 51, 234, 0.8)',
            backgroundColor: 'rgba(147, 51, 234, 0.1)',
            tension: 0.4,
            fill: true,
            pointBackgroundColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(34, 197, 94, 1)' : 'rgba(147, 51, 234, 0.8)'
            ),
            pointBorderColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(34, 197, 94, 1)' : 'rgba(147, 51, 234, 0.8)'
            ),
            pointRadius: dailyData.map((_, idx) => idx === catchIndex ? 8 : 4),
            pointBorderWidth: dailyData.map((_, idx) => idx === catchIndex ? 3 : 1),
            pointStyle: 'triangle',
          }
        ];
        yAxisConfig = {
          min: 0,
          max: 360,
          ticks: {
            callback: function(value: number) {
              return getLocalizedWindDirection(value);
            }
          },
          title: { 
            display: true, 
            text: t.windDirection || 'Vindriktning', 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          }
        };
        break;

      case 'astronomy':
        // Moon phase visualization
        datasets = [
          {
            label: `${t.moonPhase || 'Månfas'} (0=ny, 0.5=full)`,
            data: dailyData.map(d => d.moonPhase),
            borderColor: 'rgba(147, 197, 253, 0.8)',
            backgroundColor: 'rgba(147, 197, 253, 0.1)',
            tension: 0.4,
            fill: true,
            pointBackgroundColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(34, 197, 94, 1)' : 'rgba(147, 197, 253, 0.8)'
            ),
            pointBorderColor: dailyData.map((_, idx) => 
              idx === catchIndex ? 'rgba(34, 197, 94, 1)' : 'rgba(147, 197, 253, 0.8)'
            ),
            pointRadius: dailyData.map((_, idx) => idx === catchIndex ? 8 : 4),
            pointBorderWidth: dailyData.map((_, idx) => idx === catchIndex ? 3 : 1),
          }
        ];
        yAxisConfig = {
          min: 0,
          max: 1,
          title: { 
            display: true, 
            text: t.moonPhase || 'Månfas', 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          },
          ticks: {
            callback: (value: number) => {
              return getMoonPhaseIcon(value) + ' ' + getLocalizedMoonPhase(value);
            }
          }
        };
        break;
    }


    const chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: 'rgba(255, 255, 255, 0.8)',
              font: { size: 11 },
              padding: 10,
              usePointStyle: true,
            }
          },
          tooltip: {
            backgroundColor: 'rgba(13, 20, 33, 0.95)',
            titleColor: 'rgba(255, 255, 255, 0.9)',
            bodyColor: 'rgba(255, 255, 255, 0.8)',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1,
            padding: 12,
            displayColors: true,
            callbacks: {
              title: (context) => {
                const idx = context[0].dataIndex;
                const day = dailyData[idx];
                const date = new Date(day.date);
                const isCatchDay = idx === catchIndex;
                return date.toLocaleDateString('sv-SE', { 
                  weekday: 'short',
                  month: 'short', 
                  day: 'numeric'
                }) + (isCatchDay ? ` 🎣 ${t.catchDay || 'FÅNGSTDAG'}` : '');
              },
              afterBody: (context) => {
                const idx = context[0].dataIndex;
                const day = dailyData[idx];
                const extraInfo: string[] = [];
                
                // Add context-specific extra info based on active tab
                if (activeTab === 'temperature') {
                  extraInfo.push(`${t.minTemp || 'Min'}: ${convertTemperature(day.tempMin, unitSystem).toFixed(1)}${getTemperatureUnit(unitSystem)}`);
                  extraInfo.push(`${t.maxTemp || 'Max'}: ${convertTemperature(day.tempMax, unitSystem).toFixed(1)}${getTemperatureUnit(unitSystem)}`);
                } else if (activeTab === 'wind' || activeTab === 'windDirection') {
                  extraInfo.push(`${t.windDirection || 'Riktning'}: ${getLocalizedWindDirection(day.windDirection)}`);
                  if (day.windGust) {
                    extraInfo.push(`${t.windGust || 'Byar'}: ${convertWindSpeed(day.windGust, unitSystem).toFixed(1)} ${getWindSpeedUnit(unitSystem)}`);
                  }
                } else if (activeTab === 'astronomy') {
                  extraInfo.push(`${t.moonPhase || 'Månfas'}: ${getLocalizedMoonPhase(day.moonPhase)}`);
                  if (day.sunrise) extraInfo.push(`↑ ${day.sunrise.substring(11, 16)}`);
                  if (day.sunset) extraInfo.push(`↓ ${day.sunset.substring(11, 16)}`);
                } else if (activeTab === 'fishingScore' && (day as any).fishingScore) {
                  const score = (day as any).fishingScore.score;
                  const rating = score >= 70 ? (t.excellentFishing || 'Utmärkt') : score >= 50 ? (t.goodConditions || 'Bra') : (t.difficultConditions || 'Svårt');
                  extraInfo.push(`${rating}`);
                }
                
                return extraInfo;
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              color: 'rgba(255, 255, 255, 0.05)',
            },
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: { size: 11 },
              maxRotation: 0,
              minRotation: 0,
              autoSkip: false
            }
          },
          y: {
            ...yAxisConfig,
            grid: {
              color: 'rgba(255, 255, 255, 0.05)',
            },
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: { size: 11 }
            }
          }
        }
      }
    });

    chartInstanceRef.current = chart;

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [activeTab, weatherHistory, catchTimestamp, unitSystem, t, getLocalizedMoonPhase, getLocalizedWindDirection, getMoonPhaseIcon]);

  if (!weatherHistory.daily || weatherHistory.daily.length === 0) {
    return (
      <div className="rounded-2xl sm:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] shadow-lg p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-white mb-4">📊 Vädertrender</h2>
        <p className="text-slate-400 text-center py-8">Ingen väderhistorik tillgänglig</p>
      </div>
    );
  }

  // Calculate trend insights (based on daily data)
  const dailyDataSorted = [...weatherHistory.daily].sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  const firstDay = dailyDataSorted[0];
  const lastDay = dailyDataSorted[dailyDataSorted.length - 1];
  
  const tempChange = ((lastDay.tempMin + lastDay.tempMax) / 2) - ((firstDay.tempMin + firstDay.tempMax) / 2);
  const pressureChange = lastDay.pressure - firstDay.pressure;
  const avgWindSpeed = dailyDataSorted.reduce((sum, d) => sum + d.windSpeed, 0) / dailyDataSorted.length;
  
  // Moon phase insights - using shared utility
  const avgMoonPhase = dailyDataSorted.reduce((sum, d) => sum + d.moonPhase, 0) / dailyDataSorted.length;
  
  // Fishing score insights from daily data
  const fishingScores = dailyDataSorted.filter((d: any) => d.fishingScore).map((d: any) => d.fishingScore.score);
  const avgFishingScore = fishingScores.length > 0 
    ? fishingScores.reduce((sum: number, s: number) => sum + s, 0) / fishingScores.length 
    : 0;
  const maxFishingScore = fishingScores.length > 0 ? Math.max(...fishingScores) : 0;
  
  // Calculate time span
  const timeSpanDays = dailyDataSorted.length;

  return (
    <div className="rounded-2xl sm:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] shadow-lg p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">📊 {t.weatherHistory || 'Vädertrender'}</h2>
        <span className="text-xs text-slate-400">
          {timeSpanDays} {t.days || 'dagar'}
        </span>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveTab('temperature')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            activeTab === 'temperature'
              ? 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30'
              : 'bg-white/[0.03] text-slate-400 hover:bg-white/[0.05]'
          }`}
        >
          🌡️ {t.temperature || 'Temp'}
        </button>
        <button
          onClick={() => setActiveTab('pressure')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            activeTab === 'pressure'
              ? 'bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/30'
              : 'bg-white/[0.03] text-slate-400 hover:bg-white/[0.05]'
          }`}
        >
          🌊 {t.pressure || 'Tryck'}
        </button>
        <button
          onClick={() => setActiveTab('wind')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            activeTab === 'wind'
              ? 'bg-green-500/20 text-green-300 ring-1 ring-green-500/30'
              : 'bg-white/[0.03] text-slate-400 hover:bg-white/[0.05]'
          }`}
        >
          💨 {t.wind || 'Vind'}
        </button>
        <button
          onClick={() => setActiveTab('windDirection')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            activeTab === 'windDirection'
              ? 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30'
              : 'bg-white/[0.03] text-slate-400 hover:bg-white/[0.05]'
          }`}
        >
          🧭 {t.windDirection || 'Riktning'}
        </button>
        <button
          onClick={() => setActiveTab('precipitation')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            activeTab === 'precipitation'
              ? 'bg-blue-400/20 text-blue-200 ring-1 ring-blue-400/30'
              : 'bg-white/[0.03] text-slate-400 hover:bg-white/[0.05]'
          }`}
        >
          ☔ {t.precipitation || 'Nederbörd'}
        </button>
        <button
          onClick={() => setActiveTab('fishingScore')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            activeTab === 'fishingScore'
              ? 'bg-yellow-500/20 text-yellow-300 ring-1 ring-yellow-500/30'
              : 'bg-white/[0.03] text-slate-400 hover:bg-white/[0.05]'
          }`}
        >
          🎣 {t.fishingScore || 'Fiskescore'}
        </button>
        <button
          onClick={() => setActiveTab('astronomy')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            activeTab === 'astronomy'
              ? 'bg-indigo-500/20 text-indigo-300 ring-1 ring-indigo-500/30'
              : 'bg-white/[0.03] text-slate-400 hover:bg-white/[0.05]'
          }`}
        >
          🌙 {t.astronomy || 'Astronomi'}
        </button>
      </div>

      {/* Insights Row - Dynamic based on active tab */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {activeTab === 'temperature' && (
          <>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.tempChange || 'Temp förändring'}</div>
              <div className={`text-lg font-semibold ${tempChange > 0 ? 'text-red-400' : 'text-blue-400'}`}>
                {tempChange > 0 ? '↑' : '↓'} {Math.abs(convertTemperature(tempChange, unitSystem)).toFixed(1)}{getTemperatureUnit(unitSystem)}
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.minTemp || 'Min temp'}</div>
              <div className="text-lg font-semibold text-blue-300">
                {convertTemperature(Math.min(...dailyDataSorted.map(d => d.tempMin)), unitSystem).toFixed(1)}{getTemperatureUnit(unitSystem)}
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.maxTemp || 'Max temp'}</div>
              <div className="text-lg font-semibold text-red-300">
                {convertTemperature(Math.max(...dailyDataSorted.map(d => d.tempMax)), unitSystem).toFixed(1)}{getTemperatureUnit(unitSystem)}
              </div>
            </div>
          </>
        )}
        
        {activeTab === 'pressure' && (
          <>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.pressureChange || 'Tryck förändring'}</div>
              <div className={`text-lg font-semibold ${pressureChange > 0 ? 'text-green-400' : 'text-orange-400'}`}>
                {pressureChange > 0 ? '↑' : '↓'} {Math.abs(convertPressure(pressureChange, unitSystem)).toFixed(1)} {getPressureUnit(unitSystem)}
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.minPressure || 'Min tryck'}</div>
              <div className="text-lg font-semibold text-orange-300">
                {convertPressure(Math.min(...dailyDataSorted.map(d => d.pressure)), unitSystem).toFixed(0)} {getPressureUnit(unitSystem)}
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.maxPressure || 'Max tryck'}</div>
              <div className="text-lg font-semibold text-green-300">
                {convertPressure(Math.max(...dailyDataSorted.map(d => d.pressure)), unitSystem).toFixed(0)} {getPressureUnit(unitSystem)}
              </div>
            </div>
          </>
        )}
        
        {(activeTab === 'wind' || activeTab === 'windDirection') && (
          <>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.avgWind || 'Snitt vind'}</div>
              <div className="text-lg font-semibold text-white">
                {convertWindSpeed(avgWindSpeed, unitSystem).toFixed(1)} {getWindSpeedUnit(unitSystem)}
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.maxGust || 'Max by'}</div>
              <div className="text-lg font-semibold text-red-300">
                {convertWindSpeed(Math.max(...dailyDataSorted.map(d => d.windGust || d.windSpeed)), unitSystem).toFixed(1)} {getWindSpeedUnit(unitSystem)}
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">
                {activeTab === 'windDirection' ? (t.dominantDirection || 'Dominerande riktning') : (t.windSpan || 'Vind span')}
              </div>
              <div className="text-lg font-semibold text-purple-300">
                {activeTab === 'windDirection' ? (() => {
                  const avgDir = dailyDataSorted.reduce((sum, d) => sum + d.windDirection, 0) / dailyDataSorted.length;
                  return `${getLocalizedWindDirection(avgDir)} (${avgDir.toFixed(0)}°)`;
                })() : `${convertWindSpeed(Math.min(...dailyDataSorted.map(d => d.windSpeed)), unitSystem).toFixed(1)} - ${convertWindSpeed(Math.max(...dailyDataSorted.map(d => d.windSpeed)), unitSystem).toFixed(1)} ${getWindSpeedUnit(unitSystem)}`}
              </div>
            </div>
          </>
        )}
        
        {activeTab === 'precipitation' && (
          <>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.totalPrecipitation || 'Total nederbörd'}</div>
              <div className="text-lg font-semibold text-blue-300">
                {convertPrecipitation(dailyDataSorted.reduce((sum, d) => sum + d.precipitation, 0), unitSystem).toFixed(1)} {getPrecipitationUnit(unitSystem)}
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.maxPerDay || 'Max/dag'}</div>
              <div className="text-lg font-semibold text-blue-400">
                {convertPrecipitation(Math.max(...dailyDataSorted.map(d => d.precipitation)), unitSystem).toFixed(1)} {getPrecipitationUnit(unitSystem)}
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.rainyDays || 'Regndagar'}</div>
              <div className="text-lg font-semibold text-white">
                {dailyDataSorted.filter(d => d.precipitation > 1).length} {t.days || 'dagar'}
              </div>
            </div>
          </>
        )}
        
        {activeTab === 'fishingScore' && (
          <>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.avgScore || 'Snitt score'}</div>
              <div className="text-lg font-semibold text-yellow-300">
                {avgFishingScore.toFixed(0)} / 100
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.maxScore || 'Max score'}</div>
              <div className="text-lg font-semibold text-green-300">
                {maxFishingScore.toFixed(0)} / 100
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.quality || 'Kvalitet'}</div>
              <div className="text-lg font-semibold text-white">
                {avgFishingScore >= 70 ? `🟢 ${t.excellentFishing || 'Utmärkt'}` : avgFishingScore >= 50 ? `🟡 ${t.goodConditions || 'Bra'}` : `🔴 ${t.difficultConditions || 'Svårt'}`}
              </div>
            </div>
          </>
        )}
        
        {activeTab === 'astronomy' && (
          <>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.moonPhase || 'Månfas'}</div>
              <div className="text-lg font-semibold text-indigo-300">
                {getMoonPhaseIcon(avgMoonPhase)} {getLocalizedMoonPhase(avgMoonPhase)}
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.sunrise || 'Soluppgång'}</div>
              <div className="text-lg font-semibold text-orange-300">
                {weatherHistory.daily[0]?.sunrise?.substring(11, 16) || 'N/A'}
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3 border border-white/[0.08]">
              <div className="text-xs text-slate-400 mb-1">{t.sunset || 'Solnedgång'}</div>
              <div className="text-lg font-semibold text-purple-300">
                {weatherHistory.daily[0]?.sunset?.substring(11, 16) || 'N/A'}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Chart */}
      <div className="relative" style={{ height: '300px' }}>
        <canvas ref={chartRef}></canvas>
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-white/[0.08]">
        <p className="text-xs text-slate-400 text-center">
          🎣 {t.catchMomentLegend || 'Grön punkt = Fångsttillfälle'}
        </p>
      </div>
    </div>
  );
};
